'use client';

// Client despite holding no state: the rows below hand Base UI's client `render`
// prop an element of their own, and a server element there has no type in the
// client bundle — the popup then throws React #130 on open and unmounts the
// header, in a production build only.

import {
  SearchComboboxGroup,
  SearchComboboxGroupLabel,
  SearchComboboxItem,
} from '@vs-shop/ui/searchCombobox';
import {
  SearchSuggestProductBody,
  SearchSuggestProductGrid,
  SearchSuggestProductImage,
  SearchSuggestProductImagePlaceholder,
  SearchSuggestProductLabel,
  SearchSuggestProductLink,
  SearchSuggestProductPrice,
  SearchSuggestRow,
  SearchSuggestVariant,
} from '@vs-shop/commerce-ui/searchPanel';

import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';

import { getDefaultCurrency } from '../../../config/currency';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';

import { intlFormatMoney } from '../../server/intl/intlFormat';

import type { SuggestHit } from '../../../effect/schema/openapi/backend';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

type Props = {
  hits: SuggestHit[];
  heading: string;
  // Optional: live suggest closes the popover before navigating; the server-rendered
  // default panel omits it (native anchor navigation closes it on page change).
  onSelect?: () => void;
};

/**
 * The grid of product suggestions.
 *
 * The hit stops here: the image filter, the currency and the money format are
 * this shop's, and the shared rows are handed a finished URL and a finished
 * price string.
 */
export default function SuggestProductGroup({ hits, heading, onSelect }: Props) {
  if (hits.length === 0) return null;

  const currency = getDefaultCurrency();

  return (
    <SearchComboboxGroup>
      <SearchComboboxGroupLabel>{heading}</SearchComboboxGroupLabel>
      <SearchSuggestProductGrid>
        {hits.map((hit) => {
          const imageSrc =
            hit.imagePath !== null
              ? resolveImageUrl(hit.imagePath, ImageFilter.product_card_search_suggest)
              : null;

          return (
            <SearchSuggestRow key={hit.id}>
              <SearchComboboxItem
                value={hit}
                render={
                  <SearchSuggestProductLink
                    variant={SearchSuggestVariant.ROW}
                    href={hit.routerUrl}
                    onClick={onSelect}
                  />
                }
              >
                {imageSrc !== null ? (
                  <SearchSuggestProductImage
                    variant={SearchSuggestVariant.ROW}
                    src={imageSrc}
                    alt={hit.label}
                    width={80}
                    height={60}
                  />
                ) : (
                  <SearchSuggestProductImagePlaceholder variant={SearchSuggestVariant.ROW}>
                    <ImageMissingPlaceholder />
                  </SearchSuggestProductImagePlaceholder>
                )}
                <SearchSuggestProductBody>
                  <SearchSuggestProductLabel variant={SearchSuggestVariant.ROW}>
                    {hit.label}
                  </SearchSuggestProductLabel>
                  {typeof hit.price === 'number' ? (
                    <SearchSuggestProductPrice variant={SearchSuggestVariant.ROW}>
                      {intlFormatMoney({ value: { amount: hit.price, currency } })}
                    </SearchSuggestProductPrice>
                  ) : null}
                </SearchSuggestProductBody>
              </SearchComboboxItem>
            </SearchSuggestRow>
          );
        })}
      </SearchSuggestProductGrid>
    </SearchComboboxGroup>
  );
}
